/**
 * SeanEllisModal, micro-survey de PMF segundo Sean Ellis.
 *
 * Aparece automaticamente para buyers elegíveis (perfil completo + 3+ sessões).
 * O endpoint backend decide elegibilidade, este componente apenas perguntará
 * uma vez por sessão e respeita snooze + completion.
 *
 * Não usa window.confirm (sandbox iframe issue). Modal React puro.
 */
import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_FLAG = 'pmf_survey_shown_this_session';

// Mantém sincronização literal com routes/pmf_survey.py
const OPTIONS_FALLBACK = [
  { value: 'muito_desiludido',  label: 'Muito desiludido' },
  { value: 'algo_desiludido',   label: 'Algo desiludido' },
  { value: 'nao_desiludido',    label: 'Não ficaria desiludido' },
  { value: 'nao_aplicavel',     label: 'Não uso o suficiente para responder' },
];

export default function SeanEllisModal() {
  const { user, isAuthenticated, loading } = useAuth();
  const { pathname } = useLocation();
  const userType = user?.user_type;
  const isIntermediaryRoute = pathname.startsWith('/intermediary');
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(OPTIONS_FALLBACK);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('question'); // 'question' | 'comment' | 'thanks'

  // Check eligibility once per browser session.
  useEffect(() => {
    if (loading || !isAuthenticated || userType !== 'buyer' || isIntermediaryRoute) return;
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(SESSION_FLAG)) return;
    let alive = true;
    fetch(`${API_URL}/api/pmf-survey/eligibility`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data || !data.eligible) return;
        setQuestion(data.question);
        if (Array.isArray(data.options) && data.options.length) {
          setOptions(data.options);
        }
        sessionStorage.setItem(SESSION_FLAG, '1');
        // Pequeno delay para não interromper imediato após login
        setTimeout(() => setOpen(true), 3500);
      })
      .catch(() => { /* silently ignore */ });
    return () => { alive = false; };
  }, [loading, isAuthenticated, userType, isIntermediaryRoute]);

  const submit = useCallback(async (value, withComment) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/pmf-survey/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: value, comment: withComment || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStep('thanks');
      setTimeout(() => setOpen(false), 2200);
    } catch (e) {
      toast.error('Não foi possível enviar a resposta. Tenta de novo mais tarde.');
    } finally {
      setSubmitting(false);
    }
  }, []);

  const snooze = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/pmf-survey/snooze`, {
        method: 'POST', credentials: 'include',
      });
    } catch (_) { /* ignore */ }
    setOpen(false);
  }, []);

  const handlePick = (value) => {
    setSelected(value);
    // "Muito desiludido" e "Algo desiludido" pedem follow-up qualitativo
    if (value === 'muito_desiludido' || value === 'algo_desiludido') {
      setStep('comment');
    } else {
      submit(value, null);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
        data-testid="pmf-modal-overlay"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative"
          style={{ color: 'var(--brand-ink, #111827)' }}
          data-testid="pmf-modal"
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                <Heart className="w-4 h-4 text-purple-700" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-purple-700">
                  Microsondagem · 30 seg
                </p>
                <p className="text-xs text-gray-500 mt-0.5">A tua resposta ajuda a moldar o miD.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={snooze}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700 shrink-0"
              aria-label="Fechar e voltar a perguntar mais tarde"
              data-testid="pmf-snooze-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {step === 'question' && (
              <>
                <h2 className="text-lg font-black tracking-tight mb-5 text-gray-900 leading-tight">
                  {/* "miD" keeps its case inside the uppercase heading (brand switch). */}
                  {(question || 'Como te sentirias se já não pudesses usar o miD?').split(/(miD)/).map((part, i) => (
                    part === 'miD' ? <span key={i} className="brand-name">miD</span> : part
                  ))}
                </h2>
                <div className="space-y-2">
                  {options.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handlePick(opt.value)}
                      disabled={submitting}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all text-left disabled:opacity-50 group"
                      data-testid={`pmf-option-${opt.value}`}
                    >
                      <span className="text-sm font-semibold text-gray-900 group-hover:text-purple-900">
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 text-center mt-4">
                  Anónima · não partilhada com agentes ou parceiros.
                </p>
              </>
            )}

            {step === 'comment' && (
              <>
                <h2 className="text-lg font-black tracking-tight mb-3 text-gray-900">
                  Em poucas palavras, o que mais te ajudaria perder?
                </h2>
                <p className="text-xs text-gray-500 mb-4">
                  Opcional. Ignorar e enviar a resposta apenas.
                </p>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ex: A clareza do que procuro, os matches sem perder tempo a procurar..."
                  rows={3}
                  className="w-full rounded-2xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-purple-400 resize-none"
                  style={{ color: 'var(--brand-ink, #111827)' }}
                  maxLength={500}
                  data-testid="pmf-comment-input"
                />
                <p className="text-[10px] text-right text-gray-400 mt-1">{comment.length}/500</p>
                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => submit(selected, null)}
                    disabled={submitting}
                    className="flex-1 py-2.5 rounded-2xl border-2 border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                    data-testid="pmf-skip-comment-btn"
                  >
                    Ignorar
                  </button>
                  <button
                    type="button"
                    onClick={() => submit(selected, comment.trim() || null)}
                    disabled={submitting}
                    className="flex-1 py-2.5 rounded-2xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 disabled:opacity-50"
                    data-testid="pmf-submit-comment-btn"
                  >
                    {submitting ? 'A enviar…' : 'Enviar'}
                  </button>
                </div>
              </>
            )}

            {step === 'thanks' && (
              <div className="text-center py-4" data-testid="pmf-thanks">
                <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <Heart className="w-6 h-6 text-purple-700" strokeWidth={2.5} />
                </div>
                <h2 className="text-lg font-black text-gray-900 mb-1">Obrigado.</h2>
                <p className="text-sm text-gray-500">
                  A tua resposta vai ajudar-nos a construir um miD mais útil para ti.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
